import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import type SessionService from 'walden-fast/services/session';
import type UserService from 'walden-fast/services/user';
import type FilterService from 'walden-fast/services/filter';
import type ImpersonateService from 'walden-fast/services/impersonate';
import type KpiService from 'walden-fast/services/kpi';
import { IMPERSONATE_ACCOUNT_ID_KEY } from 'walden-fast/constants/local-storage-key';
import { SUPPORT_EMAIL } from 'walden-fast/constants/utils';

export default class ErrorController extends Controller {
  @service('router') declare routerService: RouterService;
  @service('session') declare sessionService: SessionService;
  @service('user') declare userService: UserService;
  @service('filter') declare filterService: FilterService;
  @service('impersonate') declare impersonateService: ImpersonateService;
  @service('kpi') declare kpiService: KpiService;

  declare model: unknown;

  get supportEmail() {
    return SUPPORT_EMAIL;
  }

  // Function to classify error
  classifyError(error?: unknown): boolean {
    if (!error) return true;

    // If it's an Error object
    if (error instanceof Error) {
      // Network error or fetch fail
      if (error.message.includes('Failed to fetch')) {
        return true; // only app refresh
      }
      // Other JS runtime -> hard refresh
      return false;
    }

    // If there's no error, we can try app refresh
    return true;
  }

  get isRecoverable(): boolean {
    return this.classifyError(this.model);
  }

  @action
  retry() {
    if (this.isRecoverable) {
      this.routerService.refresh();
    } else {
      window.location.reload();
    }
  }

  @action
  async logout() {
    try {
      // Clear all cache and logout
      this.clearSessionCache();
      await this.userService.logout();
    } catch (error) {
      console.error('Logout failed:', error);
      // Force hard refresh if logout fails
      window.location.reload();
    }
  }

  private clearSessionCache() {
    // Clear various caches
    this.userService.resetUser();
    this.kpiService.resetKPIs();

    // Stop impersonation if there's an active impersonation session
    if (this.impersonateService.impersonationAccount) {
      this.impersonateService.stopImpersonation();
    }

    // Clear localStorage items
    window.localStorage.removeItem('language');
    window.localStorage.removeItem(IMPERSONATE_ACCOUNT_ID_KEY);

    // Clear any other cached data
    if (window.sessionStorage) {
      window.sessionStorage.clear();
    }
  }
}
